import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Alert, Chip, Divider, styled, useTheme } from '@mui/material';
import { useFlow } from "../context/FlowContext";
import { useSettings } from '../context/SettingsContext';
import { useI18N } from '../context/I18NContext';
import { BoxContainer } from '../layouts/Theme';
import useDataStore from '../store/useDataStore';
import useWidgetStore from '../store/useWidgetStore';
import { FormContainer, FormErrorProvider, useForm } from 'react-hook-form-mui';
import { useNavigate } from 'react-router-dom';
// alias the Cart, as we also need the Cart type
import CartComponent from '../partials/checkout/Cart';
import PollingPaymentStatus from '../partials/checkout/PollingPaymentStatus';
import ErrorAlert from '../partials/ErrorAlert';
import SuccessAlert from '../partials/SuccessAlert';
import { PaymentOptionMembershipBuy, PaymentOptionMembershipMandate, PaymentOptionMembershipUse, PaymentOptionProviderAlternate, PaymentOptionProviderAlternates, PaymentOptionProviderExternal, PaymentOptionProviderNone, PaymentOptionProviderProductsOnly, PaymentOptionProviderRemote, PaymentOptionSubscriptionBuy, PaymentOptionSubscriptionUse } from './Payment/PaymentOptions';
import { useUpdateCart } from '../hooks/query/useUpdateCart';
import { useAbort } from '../hooks/query/useAbort';
import { createRow, moneyFormat } from '../helpers/general';
import { membershipCoverage, membershipLimitState, membershipUncoveredTotal } from '../helpers/membership';
import ConfigHolder from '../config/ConfigHolder';
export default function Payment(props) {
    var _a, _b, _c;
    const flow = useFlow();
    const theme = useTheme();
    const navigate = useNavigate();
    const formContext = useForm({});
    const [error, setError] = useState();
    const [paymentError, setPaymentError] = useState();
    const [redirectUri, setRedirectUri] = useState('');
    const [submitLabel, setSubmitLabel] = useState('Betaling afronden');
    const [subTotal, setSubTotal] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState();
    // the payment method we are displaying. This does not set the actual paymentMethod which triggers a lot of stuff
    const [displayPaymentMethod, setDisplayPaymentMethod] = useState();
    const [paymentOption, setPaymentOption] = useState("full");
    const [submitMode, setSubmitMode] = useState("submit");
    // When a membership/subscription submission leaves a remainder, `displayPaymentMethod` stays
    // whatever the subscription selector was ("membership_179") - never the actual online method
    // that ends up getting charged, which the response never names either (Mollie's own hosted
    // page is what lets the consumer pick a bank). PollingPaymentStatus looks its label/icon up by
    // matching `method` against the configured providers, so left as the selector it falls back to
    // "Niet afrekenen" while a real payment is in progress. This overrides that display only for
    // that case; every other submission leaves it unset and keeps using displayPaymentMethod as
    // before.
    const [pollingMethod, setPollingMethod] = useState();
    // A tab opened synchronously on click, for a membership/subscription submission that may turn
    // out to need an online payment for its remainder. The updateCart response deciding that
    // arrives asynchronously, too late to open a tab without it being popup-blocked - so this one
    // is opened blank up front and pointed at Mollie, or closed, once the response is in.
    const pendingRedirectTab = useRef(null);
    // if the view unmounts (navigated away, etc.) before the response arrives, don't leave a
    // blank tab open with nothing left to point it at
    useEffect(() => () => {
        if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
            pendingRedirectTab.current.close();
        }
    }, []);
    const [errorValidate, setErrorValidate] = useState();
    const [successValidate, setSuccessValidate] = useState();
    // has memberships or subscriptions that we can either buy or use
    const [hasValidAlternates, setHasValidAlternates] = useState(false);
    const [hasValidUsables, setHasValidUsables] = useState(false);
    const [hasValidMoreAlternates, setHasValidMoreAlternates] = useState(false);
    // has Mollie methods, like ideal, klarna etc
    const [hasValidExternals, setHasValidExternals] = useState(false);
    // show the list of alternates we can use
    const [showAlternates, setShowAlternates] = useState(false);
    // show the list of Mollie methods
    const [showExternals, setShowExternals] = useState(false);
    // show the rest of the alternate we can buy
    const [showMoreAlternates, setShowMoreAlternates] = useState(false);
    // Set once updateCart has replaced the payment methods with the providers that can pay for a
    // subscription being bought. From that point updateCart owns them and the getSubscriptionTypes
    // fallback below must stand down, or it drags us back to the subscription phase.
    const [payingForSubscription, setPayingForSubscription] = useState(false);
    const [useDeposit, setUseDeposit] = useState(false);
    const [canDeposit, setCanDeposit] = useState(false);
    const [depositPrice, setDepositPrice] = useState(0);
    const [cartFees, setCartFees] = useState();
    const [loader, setLoader] = useState(false);
    const [enableSubmit, setEnableSubmit] = useState(false);
    const [isFinal, setIsFinal] = useState(false);
    // const [isPaying, setIsPaying] = useState<boolean>(false)
    const [requiresAdditionalPayment, setRequiresAdditionalPayment] = useState(false);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [hasAppointmentProducts, setHasAppointmentProducts] = useState(false);
    const { cart, setCart, consumer, booking, paymentMethods, setPaymentMethods, paymentOptions, setPaymentOptions, fees, setFees, isPaying, setIsPaying, getDaoAppointments, getDaoProducts, selectedCapacity, selectedApptype, selectedResource } = useWidgetStore();
    const { apptypes, resources, products, } = useDataStore();
    const { api_key, widget_id, payment, hasCheckout, widgetMode } = useSettings();
    const { t, ts } = useI18N();
    const formContextUpdateCart = useForm({});
    const updateCartQuery = useUpdateCart(formContextUpdateCart);
    const abortQuery = useAbort();
    // While an abort is in flight the order it cancels still exists server side, so every
    // updateCart fired in that window races it and comes back complaining about the time slot.
    // Those answers overwrite the error that started the abort in the first place. A ref rather
    // than abortQuery.isFetching, because the gate has to close in the same tick as the refetch
    // that opens it, not on the render after.
    const abortInFlight = useRef(false);
    // Where to go once the abort of the pending order is done: the pencil on the cart takes the consumer
    // back to change the appointments, and the order holds those slots until it is aborted. Navigating first
    // would load the times while they are still claimed - and leave the order, and the lines it put in the
    // cart, behind for good, since nothing on the way back aborts it (the summary does, but this skips it).
    const leaveTo = useRef();
    // Same idea as leaveTo, for backCallback's "stay on this page, just reset the options list"
    // case: refetching the cart before the pending order is actually released means the backend's
    // own delete-old-then-create-new rebuild collides with the appointments it is about to delete
    // itself - it creates the new ones (as 'provisional') before it gets to releasing the old ones,
    // the create step fails on the still-held slot, and the just-created provisional appointments
    // are never rolled back (confirmed live: they sit there as orphaned status=4 rows until a
    // cronjob eventually sweeps them). Set true right before startAbort() so the abortQuery.data
    // effect below knows to run the refetch only once the abort has actually finished.
    const pendingBackReset = useRef(false);
    const editAppointments = (path) => {
        if (!cart.order_id || cart.order_id.startsWith('tmp_')) {
            // nothing on the server to let go of: 'tmp_' is the placeholder for an order that was never created
            navigate(`/oa_widget_${path}`);
            return;
        }
        if (leaveTo.current) {
            return;
        }
        leaveTo.current = path;
        setLoader(true);
        startAbort();
    };
    const startAbort = () => {
        if (abortInFlight.current) {
            return;
        }
        abortInFlight.current = true;
        abortQuery.refetch();
    };
    const refetchCart = () => {
        if (abortInFlight.current) {
            return;
        }
        updateCartQuery.refetch();
    };
    // A payment option the consumer can see and has actually checked. `paymentMethod` and
    // `displayPaymentMethod` are not that on their own: 'alternate' and 'external' only say which
    // list we are showing, and a method left over from an earlier pass through this view (going
    // back, changing the appointment type) belongs to a radio that is no longer rendered.
    // (a disabled option can be shown as checked - the membership that is over its limits - but it is not a choice)
    const hasCheckedPaymentMethod = () => document.querySelectorAll('.Mui-checked input[name=paymentMethod]:not(:disabled)').length > 0;
    // Whether there is anything to pick at all. An order with nothing left to pay renders no
    // options, and there the button only confirms.
    const hasPaymentMethodOptions = () => document.querySelectorAll('input[name=paymentMethod]').length > 0;
    useEffect(() => {
        var _a, _b;
        // An apptype payable only by subscription has no appointment line in the cart — prepareCart fails
        // for it and the summary sets the payment methods by hand — so counting cart lines alone reported
        // "no appointment" and sent the effect below down its products-only branch, which enables the
        // submit button unconditionally. The booking is the appointment, whether or not a line exists for
        // it yet; this matches how the summary decides the same thing.
        setHasAppointmentProducts(cart.lines.filter((line) => line.origin === 'appointment').length > 0
            || ((_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0);
    }, [cart.lines, booking.appointments]);
    useEffect(() => {
        if (hasAppointmentProducts) {
            setTimeout(() => {
                if (showExternals && subTotal === 0) {
                    setEnableSubmit(true);
                }
                else {
                    setEnableSubmit(hasCheckedPaymentMethod());
                    // A subscription whose balance cannot cover every appointment has no radio of its own: the
                    // options shown for it are its "_addSubscription" / "_addPayment" variants, and the
                    // consumer has to pick one. Nothing is checked until they do, which is not a stale
                    // selection - resetting it here would only have the preselect below set it again, and
                    // so on, re-rendering the whole view twice a second.
                    let awaitsVariantChoice = displayPaymentMethod !== undefined
                        && document.querySelectorAll(`input[name=paymentMethod][value^="${displayPaymentMethod}_add"]`).length > 0;
                    if (!hasCheckedPaymentMethod() && displayPaymentMethod !== undefined && !awaitsVariantChoice) {
                        // if we have no payment method checked, but there still is a selection, we got here via alternate flows ( f.e., edit the appointment while already into the payment proces)
                        // and since we can only catch the initial render once, we reset it here
                        // this is a bit hacky, a proper fix would rewrite the cart summary component to somehow "talk" to the view it's in ( as it's a generic component, it can't right now )
                        setDisplayPaymentMethod(undefined);
                        setPaymentMethod(undefined);
                    }
                }
            }, 500);
        }
        else {
            // no appointments
            // check if the payment method selected is actually valid. To be precise: if the consumer has a subscription or membership
            // this will be pre-filled elsewhere
            if ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription_')) || (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership_'))) {
                if (!paymentMethods.includes('alternate')) {
                    if (paymentMethods.includes('external')) {
                        setPaymentMethod('external');
                        setDisplayPaymentMethod('external');
                    }
                    else {
                        setPaymentMethod(paymentMethods[0]); // always 'none'?
                        setDisplayPaymentMethod(paymentMethods[0]);
                    }
                }
            }
            else if (paymentMethod !== 'external') {
                setEnableSubmit(true);
            }
            else {
                setEnableSubmit(false);
                setSubmitLabel('Afrekenen');
            }
        }
    }, [displayPaymentMethod, paymentMethod, submitMode, loader, hasAppointmentProducts]);
    useEffect(() => {
        if (error && error.length > 0) {
            setFees([]);
            setCartFees([]);
            refetchCart();
        }
    }, [error]);
    // this monitors the polling status, and goes to the next step when the payment is finalized
    useEffect(() => {
        var _a, _b, _c;
        if (cart.currentPayment) {
            let failed = true;
            if (cart.currentPayment.status === "success" && ((_b = (_a = cart.currentPayment) === null || _a === void 0 ? void 0 : _a.transaction) === null || _b === void 0 ? void 0 : _b.final)) {
                failed = false;
                setIsPaying(false);
                setIsFinal(false);
                if (flow.onNext) {
                    flow.onNext();
                }
            }
            else if (cart.currentPayment.status === "open") {
                failed = false;
            }
            else if (cart.currentPayment.status === "failed") {
                setPaymentError([t("Er is iets misgegaan bij het betalen. Probeer het later opnieuw.", "payment")]);
            }
            else if (cart.currentPayment.status === "cancelled") {
                setPaymentError([t("Je hebt de betaling geannuleerd", "payment")]);
            }
            else if (cart.currentPayment.status === "expired") {
                setPaymentError([t("De betaling is verlopen. Probeer het later opnieuw.", "payment")]);
            }
            else {
                // Any other status (Mollie's own "pending"/"inprogress", or anything not explicitly
                // handled above) is not a terminal one - only an explicit failed/cancelled/expired
                // is. Treated the same as "open": keep polling, don't show an error or reset the
                // cart for a payment that may still complete successfully.
                failed = false;
            }
            if (failed) {
                setIsPaying(false);
                setIsFinal(false);
                // The subscription or membership we tried to buy is a line in the cart, and getDaoProducts
                // turns every non-appointment line into a product on the next updateCart. The backend reads
                // that as a second one to buy and puts it alongside the one it already adds for the
                // appointment, so the cart doubles. That line is what has to go — the products stay, or a
                // retry after a failed payment would be for the appointment alone.
                let payingWith = formContextUpdateCart.getValues('paymentMethod');
                setCart(Object.assign(Object.assign({}, cart), { 
                    // the method the update will actually be sent with, not the `paymentMethod` state:
                    // picking iDEAL to pay for the strippenkaart leaves that state on 'ideal' while the
                    // update still goes out as subscription_51
                    lines: (payingWith === null || payingWith === void 0 ? void 0 : payingWith.startsWith('subscription')) || (payingWith === null || payingWith === void 0 ? void 0 : payingWith.startsWith('membership'))
                        ? cart.lines.filter((line) => !["subscription", "membership"].includes(line.productType))
                        : cart.lines, currentPayment: undefined }));
                setDisplayPaymentMethod(undefined);
                let radio = document.querySelector(`input[name=paymentMethod][value=${displayPaymentMethod}]`);
                if (radio) {
                    //@ts-ignore
                    (_c = radio.parentNode) === null || _c === void 0 ? void 0 : _c.click();
                }
                if (!updateCartQuery.isRefetching) {
                    refetchCart();
                }
            }
        }
    }, [(_a = cart === null || cart === void 0 ? void 0 : cart.currentPayment) === null || _a === void 0 ? void 0 : _a.status]);
    // paymentmethods is set by the summary, but could happen anywhere, so we listen to it here and update our options
    // accordingly
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (paymentMethods.length > 0) {
            // 'alternate' is pushed onto paymentMethods by the summary, but only when getSubscriptionTypes had
            // already answered by the time prepareCart did. Submitting the summary refetches both at once, so on
            // a slower backend prepareCart wins the race and 'alternate' never makes it into the list: the
            // consumer arrives here with nothing but 'none', the subscription step is skipped entirely and the
            // booking is confirmed straight away. Do not trust the response that raced — getSubscriptionTypes has
            // already told us whether there is anything to use or to buy for this appointment (it is queried per
            // apptype and date, and the list is filtered down to what is owned, buyable or mandatable), so decide
            // it from that here. Same guard as the summary's: only when this order actually holds an appointment.
            let hasAppointment = cart.lines.filter((line) => line.origin === 'appointment').length > 0
                || ((_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0, hasAlternates = paymentMethods.includes("alternate")
                || (!payingForSubscription && hasAppointment && ((_d = (_c = consumer.subscriptions) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0);
            setHasValidAlternates(hasAlternates);
            setHasValidExternals(paymentMethods.includes("external")); //|| paymentMethods.includes('none'))
            if (hasAlternates && consumer.subscriptions) {
                // if we have memberships or subscriptions to use/buy, start on that list
                let usables = (_e = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _e === void 0 ? void 0 : _e.filter((subscription) => !!subscription.subscriptionId);
                setDisplayPaymentMethod("alternate");
                setHasValidUsables(usables.length > 0);
                if (usables.length === 0) {
                    // show only the subscriptions we can  buy
                    setHasValidMoreAlternates(true);
                    setShowMoreAlternates(true);
                    setShowAlternates(false);
                }
                else if (consumer.subscriptions.length > usables.length) {
                    // show the subscriptions we can  use,. and there are others we can buy
                    setShowAlternates(true);
                    setShowMoreAlternates(false);
                    setHasValidMoreAlternates(true);
                }
                else {
                    // we  can only use
                    setShowAlternates(true);
                    setShowMoreAlternates(false);
                    setHasValidMoreAlternates(false);
                }
            }
            else if (hasAlternates && !consumer.subscriptions) {
                // be can only buy
                setShowMoreAlternates(true);
                setHasValidMoreAlternates(true);
            }
            else if (paymentMethods.includes("external")) {
                // if we have other Mollie methods, start on that list
                setDisplayPaymentMethod("external");
                setShowExternals(true);
            }
            else {
                // we have eiher 'none' or no methods at all, so this is only a confirm button
            }
        }
    }, [paymentMethods, consumer.subscriptions, cart.lines, booking.appointments, payingForSubscription]);
    useEffect(() => {
        // @ts-ignore
        setDepositPrice(paymentOptions.deposit && paymentOptions.deposit > 0 ? paymentOptions.deposit / 100 : 0);
        // @ts-ignore
        setCanDeposit(!!(paymentOptions.deposit && paymentOptions.deposit > 0));
    }, [paymentOptions]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (updateCartQuery.data) {
            setLoader(false);
            let response = updateCartQuery.data;
            if (response.status.status === 'success') {
                let result = response.result.item;
                // we check these together as we only need 1 navigate to thankyou
                if ((result === null || result === void 0 ? void 0 : result.redirect) && ((_a = result === null || result === void 0 ? void 0 : result.redirect) === null || _a === void 0 ? void 0 : _a.uri)) {
                    // An app type payable only by a subscription/membership never went through
                    // prepareCart (it can't price a bare appointment - see Summary.tsx), so
                    // cart.order_id is still the 'tmp_<hash>' placeholder Summary.tsx sets just to
                    // make this view exist at all. This response is the first place the REAL order
                    // id becomes known for that case - a subscription/membership submission that
                    // redirects straight to Mollie only ever ran the branch further below (result.
                    // order.order_id), which never runs when there's a redirect. Left unfixed,
                    // PollingPaymentStatus below is handed that stale 'tmp_' id as paymentId and
                    // polls a payment that can never exist under it - confirmed live, the poll
                    // payload really does carry 'tmp_...' forever until the consumer aborts and
                    // retries (by which point order_id has already been corrected once, from an
                    // earlier pass through the non-redirect branch further below).
                    //
                    // Folded into whichever setCart below actually runs (rather than its own call)
                    // on purpose: setCart here replaces the whole cart, and two separate calls in
                    // this same pass would each spread the same stale `cart` closure - the second
                    // one would silently undo the first's order_id fix.
                    const orderId = (_c = (_b = result.order) === null || _b === void 0 ? void 0 : _b.order_id) !== null && _c !== void 0 ? _c : cart.order_id;
                    if (result.subscriptions && result.subscriptions.length > 0) {
                        // The order is submitted, so these are the subscriptions that paid for the
                        // appointments and what they have left - the thank you page lists them. One that
                        // the consumer did not own before this order was bought with it; the consumer's
                        // own list has not been refreshed yet, so it still says which those are.
                        // (the consumer's list carries the id as it came from the api, a string, while the
                        // order's subscriptions have it as a number - hence the conversion)
                        let owned = ((_d = consumer.subscriptions) !== null && _d !== void 0 ? _d : [])
                            .filter((subscription) => subscription.subscriptionId)
                            .map((subscription) => Number(subscription.subscriptionId));
                        setCart(Object.assign(Object.assign({}, cart), { order_id: orderId, subscriptions: result.subscriptions.map((subscription) => {
                                var _a, _b, _c, _d;
                                let bought = !owned.includes(subscription.id);
                                return Object.assign(Object.assign({}, subscription), { type: (_b = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((type) => type.id === subscription.subscriptionType)) === null || _b === void 0 ? void 0 : _b.type, bought, price: bought
                                        ? (_d = (_c = consumer.subscriptions) === null || _c === void 0 ? void 0 : _c.find((type) => type.id === subscription.subscriptionType)) === null || _d === void 0 ? void 0 : _d.sellPrice
                                        : undefined });
                            }) }));
                    }
                    else if (orderId !== cart.order_id) {
                        setCart(Object.assign(Object.assign({}, cart), { order_id: orderId }));
                    }
                    if (result.redirect.uri === '#oa_thankyou') {
                        // the membership/subscription alone covered it - nothing for the
                        // pre-opened tab to do, so close it rather than leave it sitting blank
                        if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
                            pendingRedirectTab.current.close();
                        }
                        pendingRedirectTab.current = null;
                        setIsPaying(false);
                        setIsFinal(false);
                        if (flow.onNext) {
                            flow.onNext();
                        }
                    }
                    else {
                        // an actual online payment is happening now, whether the widget just
                        // opened it (standalone) or asked the parent page to (embedded) - a
                        // membership/subscription submission that gets here always did leave a
                        // remainder, so the widget needs to poll for it exactly like a plain
                        // online payment does
                        setIsPaying(true);
                        if ((displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) || (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription'))) {
                            setPollingMethod('ideal');
                        }
                        if (widgetMode === 'standalone') {
                            if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
                                // point the tab opened synchronously on click at Mollie, now that
                                // we know the redirect - opening one now would be popup-blocked,
                                // this far from the click that started the submission
                                pendingRedirectTab.current.location.href = (_e = result === null || result === void 0 ? void 0 : result.redirect) === null || _e === void 0 ? void 0 : _e.uri;
                            }
                            else {
                                // no tab was pre-opened for this submission - a plain online
                                // method never needs one, since the form's own target="_blank"
                                // already opened a real tab for it
                                window.location.href = (_f = result === null || result === void 0 ? void 0 : result.redirect) === null || _f === void 0 ? void 0 : _f.uri;
                            }
                            pendingRedirectTab.current = null;
                        }
                        else {
                            window.parent.postMessage({
                                type: 'redirect',
                                url: (_g = result === null || result === void 0 ? void 0 : result.redirect) === null || _g === void 0 ? void 0 : _g.uri
                            }, '*');
                        }
                    }
                }
                else {
                    // a remainder resolved into a follow-up payment-method choice rather than an
                    // immediate redirect - nothing to point the pre-opened tab at yet
                    if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
                        pendingRedirectTab.current.close();
                    }
                    pendingRedirectTab.current = null;
                    setIsPaying(false);
                    setIsFinal(false);
                    let _paymentOptions = result.paymentOptions;
                    setPaymentOptions(_paymentOptions);
                    // @ts-ignore
                    setFees(result.fees);
                    // @ts-ignore
                    setDepositPrice(_paymentOptions.deposit && _paymentOptions.deposit > 0 ? _paymentOptions.deposit / 100 : 0);
                    // @ts-ignore
                    setCanDeposit(!!(_paymentOptions.deposit && _paymentOptions.deposit > 0));
                    // these are fetched once on the summary in the prepare API
                    //setPaymentMethods(result.paymentMethods)
                    if (result.order.order_id) {
                        setCart(Object.assign(Object.assign({}, cart), { 
                            // the order lines the backend just built: the appointment plus whatever the
                            // consumer chose to buy. They carry no `origin`, which the views downstream
                            // read (hasAppointmentProducts, the optionalPurchase rows), so keep the origin
                            // of the line we already had and fall back to the product type for new ones.
                            lines: result.lines.map((line) => {
                                var _a, _b;
                                return (Object.assign(Object.assign({}, line), { origin: (_b = (_a = cart.lines.find((existing) => existing.productId === line.productId)) === null || _a === void 0 ? void 0 : _a.origin) !== null && _b !== void 0 ? _b : (line.productType === 'appointment' ? 'appointment' : 'optionalPurchase') }));
                            }), 
                            // lines: newLines, // we manage our lines internally, as we need to retain the origin and other info. It's just for display purposes anyways
                            giftCards: result.giftCards, 
                            // discounts: result.discounts, we keep track of the applied discounts locally
                            order_id: result.order.order_id, token: result.csrfToken }));
                        // A membership is its own productType, distinct from "subscription" - every
                        // other check in this file (and Cart.tsx) already covers both. This one
                        // didn't, so buying a new membership got an order_id and a real remainder
                        // to pay back from the backend, but the UI never switched to showing how to
                        // pay it: paymentMethod stayed pointed at the membership radio that had just
                        // been bought and no longer matched anything on screen, which read as the
                        // whole form silently resetting to unselected.
                        let subscriptionPayment = result.lines.find((line) => ["subscription", "membership"].includes(line.productType));
                        if (subscriptionPayment) {
                            setPayingForSubscription(true);
                            let _externalMethods = result.paymentMethods;
                            _externalMethods.push("external");
                            setPaymentMethods(_externalMethods);
                            setShowExternals(true);
                            setShowAlternates(false);
                            setShowMoreAlternates(false);
                            setHasValidExternals(true);
                            setDisplayPaymentMethod("external");
                        }
                        if (showAlternates && hasValidMoreAlternates && displayPaymentMethod === "alternate") {
                            setShowAlternates(false);
                            setShowMoreAlternates(true);
                        }
                    }
                }
            }
            else if (response.status.status === 'failed') {
                if (!((_h = cart.order_id) === null || _h === void 0 ? void 0 : _h.startsWith('tmp_')) || response.status.userMessages.includes("Abonnement of Strippenkaart niet geldig.")) {
                    setError((_j = response.status.userMessages) !== null && _j !== void 0 ? _j : []);
                }
                // a submission that opened a tab (an owned subscription/membership, see
                // openPaymentTab) never got a redirect to point it at - leave it orphaned at
                // about:blank rather than close it, and the consumer is stuck looking at nothing
                if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
                    pendingRedirectTab.current.close();
                }
                pendingRedirectTab.current = null;
                setIsPaying(false);
                setIsFinal(false);
                formContextUpdateCart.setValue("final", false);
                if (formContextUpdateCart.getValues("selectedSubscription")) {
                    formContextUpdateCart.setValue("selectedSubscription", undefined);
                    if ((_k = formContext.getValues('paymentMethod')) === null || _k === void 0 ? void 0 : _k.startsWith('membership_')) {
                        formContextUpdateCart.setValue("paymentMethod", "alternate");
                    }
                }
                startAbort();
                // if (response.status.userMessages.includes("Abonnement of Strippenkaart niet geldig.")) {
                //     abortQuery.refetch()
                // }
                // else {
                //     updateCartQuery.refetch()
                // }
            }
        }
    }, [updateCartQuery.data]);
    // updateCart rejected outright - a network failure, or a response body that never parsed as
    // JSON (a backend fatal error after it already created the online payment is the one we have
    // caught in the act: the redirect the tab was waiting for never arrives). Without this, the
    // 'data' effect above never runs, the pre-opened tab sits at about:blank forever, and the
    // consumer is left looking at "De betaalmethode wordt bijgewerkt..." with no way out.
    // Unlike the 'failed' status branch above, the backend's own outcome here is unknown - it may
    // have gone on to create a real payment after the response broke - so this does not call
    // startAbort(): that would cancel an order that might still be genuinely pending.
    useEffect(() => {
        if (updateCartQuery.isError) {
            setLoader(false);
            if (pendingRedirectTab.current && !pendingRedirectTab.current.closed) {
                pendingRedirectTab.current.close();
            }
            pendingRedirectTab.current = null;
            setIsPaying(false);
            setIsFinal(false);
            formContextUpdateCart.setValue("final", false);
            setError([t("Er is iets misgegaan bij het betalen. Probeer het later opnieuw.", "payment")]);
        }
    }, [updateCartQuery.isError]);
    useEffect(() => {
        setCartFees([]);
        if (displayPaymentMethod) {
            if (fees[displayPaymentMethod]) {
                let fee = fees[displayPaymentMethod];
                if (useDeposit && fee.deposit && fee.deposit > 0) {
                    setCartFees([{
                            name: t('Transactiekosten'),
                            value: fee.deposit / 100
                        }]);
                }
                else if (fee.full && fee.full > 0) {
                    setCartFees([{
                            name: t('Transactiekosten'),
                            value: fee.full / 100
                        }]);
                }
            }
            if (displayPaymentMethod === 'none' || (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership_')) || (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription_'))) {
                setCanDeposit(false);
                setUseDeposit(!paymentOptions.full && !!(paymentOptions.deposit && paymentOptions.deposit > 0));
            }
            else {
                setCanDeposit(!!(paymentOptions.deposit && paymentOptions.deposit > 0));
            }
            setPaymentOption(useDeposit ? 'deposit' : 'full');
        }
    }, [paymentMethod, displayPaymentMethod, useDeposit]);
    useEffect(() => {
        if (cart.mandatables && cart.mandatables[0] && cart.mandatables[0].mandatable) {
            if (flow.onNext) {
                flow.onNext();
            }
        }
    }, [cart.mandatables]);
    useEffect(() => {
        var _a, _b, _c, _d;
        let _rows = [];
        if (booking && apptypes && ((_a = booking === null || booking === void 0 ? void 0 : booking.appointments) !== null && _a !== void 0 ? _a : []).length > 0) {
            let appointment = booking.appointments[0];
            if (appointment) {
                let apptypeId = (_c = (_b = booking === null || booking === void 0 ? void 0 : booking.appointments[0]) === null || _b === void 0 ? void 0 : _b.components[0]) === null || _c === void 0 ? void 0 : _c.appointmentTypes[0];
                let apptype = apptypes.find((apptype) => apptype.id === apptypeId);
                if (apptype) {
                    _rows.push(createRow([], ((_d = appointment === null || appointment === void 0 ? void 0 : appointment.price) !== null && _d !== void 0 ? _d : 0), // this is the price set by the bookable time, which can be a sum of the components
                    1, 0, //   21,
                    "appointment", "appointment"));
                }
            }
        }
        // let _subtotal: number = subtotal(_rows)
        // setSubTotal(_subtotal)
    }, [apptypes, resources, products, booking, cart]);
    useEffect(() => {
        var _a;
        // this effect tries to preselect the first usable alternate ( subscription / membership )
        if (paymentMethods.length > 0) {
            if ((consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) && !cart.lines.find((line) => ['subscription', 'membership'].includes(line.productType))) {
                // A membership over its limits is not preselected: what it pays for depends on the
                // choice the consumer has yet to make, or, when the rest cannot be paid, it is not a choice at all.
                let usable = consumer.subscriptions.find((subscription) => subscription.subscriptionId && limitStateOf(subscription) === 'full');
                if (usable && !paymentMethod) {
                    setDisplayPaymentMethod(`${usable.type}_${usable.id}`);
                    setPaymentMethod(`${usable.type}_${usable.id}`);
                    formContext.setValue('paymentMethod', `${usable.type}_${usable.id}`);
                    let radio = document.querySelector(`input[name=paymentMethod][value=${usable.type}_${usable.id}]`);
                    if (radio) {
                        //@ts-ignore
                        (_a = radio.parentNode) === null || _a === void 0 ? void 0 : _a.click();
                    }
                }
                if (!usable && hasValidMoreAlternates && !showAlternates) {
                    // Only fall back to the "buy" list when there is nothing owned worth showing
                    // in the "use" list. A membership over its limit is not `usable` either, but
                    // it still has its own row (max bereikt / betaal restant) that showAlternates
                    // already accounts for - flipping showMoreAlternates on top of that would make
                    // both lists' row filters treat each other's rows as the ones to hide, and
                    // everything - owned and buyable alike - disappears.
                    setShowMoreAlternates(true);
                }
            }
        }
    }, [paymentMethods, paymentMethod, displayPaymentMethod, showAlternates]);
    // How an owned membership stands against the limits of the booking (see helpers/membership.ts). What the
    // membership does not pay for can only be paid online when the app type has an external payment method.
    const limitStateOf = (subscription) => { var _a, _b; return membershipLimitState(subscription, (_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0, hasValidExternals); };
    // what the appointments the membership does not pay for cost together
    const uncoveredTotalOf = (subscription) => {
        var _a, _b, _c, _d;
        return membershipUncoveredTotal((_c = membershipCoverage(subscription, (_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0)) !== null && _c !== void 0 ? _c : [], ((_d = booking.appointments) !== null && _d !== void 0 ? _d : []).map((appointment) => appointment.price));
    };
    // Whether THIS subscription's own balance falls short of every appointment - computed fresh
    // per subscription, unlike the requiresAdditionalPayment state below, which only reflects
    // whichever subscription displayPaymentMethod last pointed at. Reading that state while
    // building each subscription's own row meant every row but the currently/previously selected
    // one collapsed to the bare "full" fallback (no addSubscription/addPayment split) the moment
    // displayPaymentMethod moved elsewhere - e.g. to 'external' or 'alternate' when picking "Losse
    // afspraak maken" or "Toon overige abonnementen en strippenkaarten". The membership branch
    // never had this problem: it already computes its own state per subscription (limitStateOf,
    // uncoveredTotalOf above), which is the pattern this follows.
    const subscriptionRequiresAdditionalPayment = (subscription) => { var _a, _b; return !!subscription.charge && subscription.charge * ((_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > subscription.balance; };
    // The appointments this subscription's own balance does not cover, priced together - what the
    // addPayment radio's own "restant" text quotes. Same reasoning as
    // subscriptionRequiresAdditionalPayment above: computed per subscription, not read off state
    // tied to whichever subscription was last selected.
    const subscriptionUncoveredTotalOf = (subscription) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (!subscription.charge || subscription.charge * ((_b = (_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) <= subscription.balance) {
            return 0;
        }
        let appointmentsCount = (_d = (_c = booking.appointments) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0, coveredByBalance = Math.min(appointmentsCount, Math.floor(subscription.balance / subscription.charge)), uncoveredCount = appointmentsCount - coveredByBalance, appointmentLine = cart.lines.find((line) => line.productType === 'appointment'), 
        // cart.lines' own appointment line only exists once prepareCart/updateCart has
        // answered - before that (this view's very first render), fall back to the price the
        // booking itself already carries over from the summary step. Missing this fallback is
        // what made the very first render compute 0 here, and 0 is falsy but not `undefined` -
        // PaymentOptions.tsx's `addPayment && invoiceTotal && (...)` then rendered the literal
        // "0" instead of nothing.
        unitPrice = (_j = (_f = (_e = appointmentLine === null || appointmentLine === void 0 ? void 0 : appointmentLine.originalPrice) !== null && _e !== void 0 ? _e : appointmentLine === null || appointmentLine === void 0 ? void 0 : appointmentLine.price) !== null && _f !== void 0 ? _f : (_h = (_g = booking.appointments) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.price) !== null && _j !== void 0 ? _j : 0;
        return uncoveredCount * unitPrice;
    };
    // Whether a subscription/membership that already covers every appointment by itself still
    // leaves the cart's products to pay online - and if so, with which provider. This is NOT
    // decided by "is there a product in the cart": a product only forces an online payment when
    // the app type's productPayment is 'required' (see CheckoutController::_getCheckout(), example
    // 4 vs 5/6) - for 'optional'/'none' the products follow the appointment and are left unpaid
    // right along with it when a subscription/membership pays.
    //
    // The backend already signals this the same way for the plain paymentMethod 'none' case - a
    // '<provider>_productsOnly' entry in paymentMethods (see PaymentOptionProviderProductsOnly) -
    // but that signal is never computed for an app type whose OWN appointment payment is 'none'
    // (like this one): prepareCart can't price a bare appointment for it, so the summary hand-sets
    // paymentMethods to just ['alternate'] without ever reaching the code that would add it
    // (confirmed live - paymentMethods really is just ["alternate"] on this screen). Reading the
    // app type's own productPayment field instead (added to the apptypes API response and
    // ApptypeDataType for exactly this reason) works uniformly for every app type, this one
    // included.
    const currentApptype = () => {
        var _a, _b, _c, _d, _e;
        let apptypeId = (_e = (_d = (_c = (_b = (_a = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.components) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.appointmentTypes) === null || _e === void 0 ? void 0 : _e[0];
        return apptypes.find((apptype) => apptype.id === apptypeId);
    };
    // Preference order matches _resolveSubscriptionPaymentMethod exactly (iDeal, then Bancontact,
    // then any other external method of the company) - the provider actually used for the
    // remainder is the backend's call, not "whichever one the company happens to have listed
    // first", so the label shown here has to agree with it.
    const productPaymentProvider = () => {
        var _a, _b, _c;
        if (((_a = currentApptype()) === null || _a === void 0 ? void 0 : _a.productPayment) !== 'required')
            return undefined;
        for (const key of ['ideal', 'bancontact']) {
            const provider = (_b = payment === null || payment === void 0 ? void 0 : payment.providers) === null || _b === void 0 ? void 0 : _b.find((provider) => provider.key === key);
            if (provider)
                return provider;
        }
        return (_c = payment === null || payment === void 0 ? void 0 : payment.providers) === null || _c === void 0 ? void 0 : _c.find((provider) => !['none', 'alternate', 'external'].includes(provider.key)
            && !provider.key.startsWith('subscription_')
            && !provider.key.startsWith('membership_'));
    };
    const requiresProductPayment = (subscription) => {
        const provider = productPaymentProvider();
        if (!provider)
            return false;
        return subscription.type === 'membership'
            ? limitStateOf(subscription) === 'full'
            : !subscriptionRequiresAdditionalPayment(subscription);
    };
    const productPaymentProviderName = () => {
        const provider = productPaymentProvider();
        return provider ? t(provider.label, "payment").replace(/^Betaal met\s+/i, '') : '';
    };
    // The tab is for the online payment provider, and nothing else needs one. So it is only opened when
    // there is something to pay online: what the summary shows as still to pay is more than nothing. An
    // order the subscription or membership pays in full is finalized without leaving the widget, and a
    // tab opened for it would only flash open and shut. Should the answer turn out to want a payment after
    // all, there is no tab to point at Mollie, and the widget itself is sent there instead - the
    // fallback in the updateCart effect.
    const openPaymentTab = () => {
        if (subTotal > 0) {
            pendingRedirectTab.current = window.open('', '_blank');
        }
    };
    const onSubmit = (data, event) => {
        var _a, _b;
        // cleared on every submission, so a stale value from an earlier membership/subscription
        // attempt can't override a later one's own display
        setPollingMethod(undefined);
        let _final = false;
        formContextUpdateCart.setValue('paymentMethod', data.paymentMethod);
        formContextUpdateCart.setValue('paymentOption', paymentOption);
        localStorage.setItem('storedCart', JSON.stringify(cart));
        localStorage.setItem('storedBooking', JSON.stringify(booking));
        if (data.paymentMethod.startsWith('subscription')) {
            let subscription = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === parseInt(data.paymentMethod.substring(13)));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                subscription.active = true;
                _final = true;
                // using an existing subscription can still leave a remainder to pay online - see
                // the tab-opening comment in prepareSubmit for why this has to happen here,
                // synchronously, rather than once the response says a remainder is due
                openPaymentTab();
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
            else if (subscription === null || subscription === void 0 ? void 0 : subscription.id) {
                subscription.active = false;
                _final = false;
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
        }
        else if (data.paymentMethod.startsWith('membership')) {
            let pm = data.paymentMethod.substring(11);
            if (pm.endsWith('mandate')) {
                pm = pm.substring(0, pm.indexOf('_mandate'));
            }
            let subscription = (_b = consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.id === parseInt(pm));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                // use an existing membership - same as the subscription branch above, this can
                // still leave a remainder, so the tab is opened now while it's still synchronous
                subscription.active = true;
                _final = true;
                openPaymentTab();
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
            else {
                if (subscription === null || subscription === void 0 ? void 0 : subscription.mandatable) {
                    if (subscription) {
                        _final = false;
                        // add the selected subscription to mandatables
                        // this will trigger the flow context to navigate to the mandate form
                        setCart(Object.assign(Object.assign({}, cart), { mandatables: [subscription] }));
                    }
                }
                else {
                    // if (data.paymentMethod.endsWith('mandate')) {
                    //     console.log('buy new membership with mandate', subscription)
                    //     if (subscription) {
                    //         setCart({ ...cart, mandatables: [subscription] })
                    //     }
                    // }
                    // else 
                    if (subscription === null || subscription === void 0 ? void 0 : subscription.id) {
                        // buy a new membership, there is no option to mandate
                        subscription.active = false;
                        _final = false;
                        formContextUpdateCart.setValue('selectedSubscription', subscription);
                    }
                }
            }
        }
        else {
            _final = true;
            if (flow) {
                flow === null || flow === void 0 ? void 0 : flow.setBackCallback(undefined);
            }
        }
        _final = _final && submitMode === "submit";
        formContextUpdateCart.setValue('final', _final);
        setIsFinal(_final);
        if (_final) {
            if (!["none", "alternate"].includes(displayPaymentMethod) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription'))) {
                // dont show the polling for none,  membership and subscriptions
                setIsPaying(true);
            }
        }
        else {
            // just refetch the new cart, without the payment loader
            // this also means updating the payment methods
            refetchCart();
        }
    };
    useEffect(() => {
        if (isFinal) {
            if (subTotal === 0 && paymentMethod === "none" && displayPaymentMethod !== "none") {
                setDisplayPaymentMethod("none");
                formContext.setValue('paymentMethod', "none");
                setTimeout(() => {
                    refetchCart();
                }, 500);
            }
            else {
                refetchCart();
            }
        }
    }, [isFinal]);
    // useEffect(() => {
    //     if (subTotal === 0 && paymentMethod === "none" && displayPaymentMethod !== "none") {
    //         formContextUpdateCart.setValue('final', true)
    //         setIsFinal(true)
    //         setPaymentMethod("none")
    //     }
    // }, [subTotal, paymentMethod, displayPaymentMethod])
    const submitUrl = ConfigHolder.getApiMethodUrl(api_key, widget_id, 'updateCart', 'checkout');
    // prepare the POST to  a new tab where we redirect to Mollie
    const prepareSubmit = (event) => {
        // prepare alternate membership_55
        // true
        // console.log(submitMode, displayPaymentMethod, paymentMethod)
        // console.log(submitMode === "submit" && (
        //     ["none", "alternate"].includes(displayPaymentMethod as PaymentMethodType)) ||
        //     displayPaymentMethod?.startsWith('membership') ||
        //     displayPaymentMethod?.startsWith('subscription') ||
        //     subTotal === 0)
        // a yes, brackets
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (submitMode === "submit" && ((["none", "alternate"].includes(displayPaymentMethod)) ||
            (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) ||
            (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription')) ||
            subTotal === 0)) {
            // A membership/subscription selection never posts to /checkout directly - that
            // endpoint only understands a genuine online payment method (ideal, etc), not a
            // subscription selector like "membership_179". It goes through the context form's
            // AJAX mutation instead (onSubmit below), which knows how to resolve a subscription
            // and, if there is a remainder to pay, comes back with a Mollie redirect URI (see the
            // updateCartQuery effect below).
            //
            // Only USING an existing subscription/membership can end in that redirect - buying a
            // new one, or one that needs a mandate, routes to the mandate form instead and never
            // touches this at all. onSubmit is the one that knows which case this is, so it opens
            // the tab itself, right where it decides - still synchronously, still within this
            // click, so it isn't popup-blocked, but not for a branch that will never use it.
            setPaymentMethod(displayPaymentMethod);
            onSubmit({ paymentMethod: displayPaymentMethod }, event);
            // return false to stop the form from submitting
            event.preventDefault();
            return false;
        }
        if (submitMode === "prepare") {
            if (displayPaymentMethod === "alternate") {
                setShowAlternates(false);
                setShowExternals(false);
                setShowMoreAlternates(true);
            }
            else if (displayPaymentMethod === "external") {
                setShowAlternates(false);
                setShowMoreAlternates(false);
                setShowExternals(true);
            }
            else {
                // console.log(submitMode, displayPaymentMethod)
            }
            event.preventDefault();
            return false;
        }
        // // this gets posted into a new tab
        let _appointments = getDaoAppointments(), 
        // Without a subscription the backend keeps the order it already has and ignores these. With
        // one it rebuilds the order from them, and then leaving the products out drops them from the
        // order that is being paid. The subscription line itself is left out: the backend creates
        // that one, and posting it back buys a second one (see useUpdateCart).
        _products = getDaoProducts().filter((product) => !["subscription", "membership"].includes(product.type)), selectedSubscription = formContextUpdateCart.getValues('selectedSubscription'), _convertedAppointments = _appointments.length > 0
            ? [{
                    appTypeId: selectedApptype, // booking?.appointments[0]?.components[0]?.appointmentTypes,
                    resourceId: selectedResource,
                    capacity: selectedCapacity,
                    components: _appointments,
                    date: (_a = _appointments[0].date) !== null && _a !== void 0 ? _a : _appointments[0].time,
                    time: _appointments[0].time,
                    fields: (_b = consumer.fields) !== null && _b !== void 0 ? _b : {},
                    remark: (_c = booking.remark) !== null && _c !== void 0 ? _c : ''
                }]
            : [];
        let data = {
            order_id: (_d = cart.order_id) !== null && _d !== void 0 ? _d : '',
            giftCards: cart.giftCards,
            discounts: cart.discounts,
            appointments: _convertedAppointments,
            products: _products,
            paymentMethod: displayPaymentMethod,
            paymentOption: paymentOption,
            final: true,
            widgetUrl: window.location.href,
            subscriptionId: (_e = selectedSubscription === null || selectedSubscription === void 0 ? void 0 : selectedSubscription.id) !== null && _e !== void 0 ? _e : undefined
        };
        const payload = document.getElementById('payload');
        if (payload) {
            payload.value = JSON.stringify(data);
        }
        const csrfToken = document.getElementById('csrfToken');
        if (csrfToken) {
            csrfToken.name = (_g = (_f = cart.token) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : "";
            csrfToken.value = (_j = (_h = cart.token) === null || _h === void 0 ? void 0 : _h.token) !== null && _j !== void 0 ? _j : "";
        }
        const secureToken = document.getElementById('secureToken');
        if (secureToken) {
            secureToken.value = (_k = cart.secureToken) !== null && _k !== void 0 ? _k : "";
        }
        // for debugging, I can select trustly to console log the data, instead of posting to a new tab
        // because it's a new tab, we also start without the developer toolbar, and the tab redirect to Mollie
        // so we can never repost in that tab to see what was sent
        if (data.paymentMethod === "trustly") {
            console.log(data);
            event.preventDefault();
            return false;
        }
        else {
            setTimeout(() => {
                // this is a tiny bit of delay, because otherwise we would rerender instantly, causing the actual
                // POST to never proceed
                setIsPaying(true);
            }, 250);
        }
    };
    useEffect(() => {
        if (!abortQuery.data) {
            return;
        }
        abortInFlight.current = false;
        if (leaveTo.current) {
            // the order is released: what it added to the cart goes with it, like on the summary.
            // A subscription/membership line only existed to pay for the appointment this abort
            // just released, and never got bought, so it must not survive as a stray line either.
            let path = leaveTo.current;
            leaveTo.current = undefined;
            setCart(Object.assign(Object.assign({}, cart), { order_id: undefined, lines: cart.lines.filter((line) => line.origin !== 'appointment' && !['subscription', 'membership'].includes(line.productType)) }));
            navigate(`/oa_widget_${path}`);
            return;
        }
        if (pendingBackReset.current) {
            // backCallback deferred its own reset+refetch until here - see pendingBackReset above
            pendingBackReset.current = false;
            setCart(Object.assign(Object.assign({}, cart), { order_id: undefined, lines: cart.lines.filter((line) => line.origin !== 'appointment' && !['subscription', 'membership'].includes(line.productType)) }));
            applyBackReset();
            return;
        }
        let currentRoute = location.hash.substring(12);
        if (cart.order_id && currentRoute === "payment") {
            // Same reasoning as above: a subscription/membership line only existed to pay for
            // the appointment this abort just released.
            let productLines = cart.lines.filter((line) => line.origin !== 'appointment' && !['subscription', 'membership'].includes(line.productType));
            setCart(Object.assign(Object.assign({}, cart), { order_id: undefined, lines: productLines }));
            setDisplayPaymentMethod(undefined);
            setPaymentMethod(undefined);
            refetchCart();
        }
    }, [abortQuery.data]);
    useEffect(() => {
        // an abort that fails would otherwise leave the gate shut for good
        if (abortQuery.isError) {
            abortInFlight.current = false;
            if (leaveTo.current) {
                // still go where the consumer asked. The order stays in the cart, so the summary they come
                // back through tries to abort it again
                let path = leaveTo.current;
                leaveTo.current = undefined;
                setLoader(false);
                navigate(`/oa_widget_${path}`);
            }
        }
    }, [abortQuery.isError]);
    // The part of "go back to the options list" that only makes sense once any pending order is
    // actually gone - recomputing which list to show and refetching the cart. Shared between
    // backCallback (nothing to release, so this runs immediately) and the abortQuery.data effect
    // (something WAS released, so this runs once that abort has actually finished - see
    // pendingBackReset above for why that ordering matters).
    const applyBackReset = () => {
        let _showAlternates = hasValidAlternates && hasValidUsables, _showMoreAlternates = hasValidMoreAlternates && !hasValidUsables;
        setShowAlternates(_showAlternates);
        setShowMoreAlternates(_showMoreAlternates);
        setShowExternals(hasValidExternals && !_showAlternates && !_showMoreAlternates);
        setDisplayPaymentMethod(hasValidAlternates ? "alternate" : (hasValidExternals ? "external" : undefined));
        setLoader(false);
        refetchCart();
    };
    const backCallback = () => {
        var _a;
        if (flow) {
            flow === null || flow === void 0 ? void 0 : flow.setBackCallback(undefined);
            let currentRoute = location.hash.substring(12);
            if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route.startsWith("mandate")) || currentRoute.startsWith("mandate")) {
                if (!abortQuery.isRefetching) {
                    startAbort();
                }
            }
            else {
                setIsFinal(false);
                setIsPaying(false);
                setPaymentMethod(undefined);
                setPayingForSubscription(false);
                // setIsFinal(false) above resets the React state, but refetchCart() (in
                // applyBackReset, below) reads the FORM's own 'final' field (see
                // useUpdateCart.ts), which onSubmit left at whatever it last submitted - true, for
                // a membership/subscription payment that already went through. Left alone, this
                // refetch reposts that stale, already-processed submission verbatim instead of a
                // plain cart refresh, which collides with the appointment/order it already just
                // finalized ("Gekozen tijdstip is niet meer beschikbaar").
                formContextUpdateCart.setValue('final', false);
                if (showAlternates && formContextUpdateCart.getValues('selectedSubscription')) {
                    // remove the subscription we selected, and start fresh
                    formContextUpdateCart.setValue("selectedSubscription", undefined);
                }
                if (cart.order_id && !cart.order_id.startsWith('tmp_')) {
                    // there is a real order to release first - see pendingBackReset above for why
                    // refetching before that finishes is what left orphaned provisional
                    // appointments behind
                    setLoader(true);
                    pendingBackReset.current = true;
                    if (!abortQuery.isRefetching) {
                        startAbort();
                    }
                }
                else {
                    // 'tmp_' is the placeholder for an order that was never created - nothing on
                    // the server to let go of, so there is nothing to wait for either
                    applyBackReset();
                }
            }
        }
    };
    useEffect(() => {
        if (requiresAdditionalPayment && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.endsWith('addSubscription')) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.endsWith('addPayment'))) {
            setEnableSubmit(false);
        }
        else {
            // no longer requiring an additional payment is not a choice the consumer made, so as
            // long as there are options on screen one of them has to be checked before the button
            // is armed.
            setEnableSubmit(!hasPaymentMethodOptions() || hasCheckedPaymentMethod());
        }
        // the options on screen change with the list we are showing and with the cart being
        // refetched, so re-check whenever any of that moves
    }, [requiresAdditionalPayment, displayPaymentMethod, paymentMethods, showAlternates, showMoreAlternates, showExternals, subTotal, loader, updateCartQuery.isFetching]);
    const onChange = (event) => {
        var _a, _b, _c;
        let newPaymentMethod = event.currentTarget.value;
        formContext.setValue('paymentMethod', newPaymentMethod);
        setDisplayPaymentMethod(newPaymentMethod);
        if ((newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('subscription')) || (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('membership'))) {
            setPaymentMethod(newPaymentMethod);
            if (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('subscription')) {
                let subscriptionId = parseInt(newPaymentMethod.substring(13)), subscription = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === subscriptionId), usable = !!(subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId), requiredSubscriptions = 1;
                if (!usable) {
                    if (subscription) {
                        let requiredCredits = booking.appointments.length * subscription.charge;
                        requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                    }
                }
                setSubmitMode("submit");
                setSubmitLabel(usable
                    ? requiresAdditionalPayment
                        ? newPaymentMethod.endsWith('addSubscription')
                            ? 'Strippenkaart gebruiken en aanvullen'
                            : 'Strippenkaart gebruiken en restant afrekenen'
                        : subscription && requiresProductPayment(subscription)
                            ? 'Strippenkaart gebruiken en producten afrekenen'
                            : 'Strippenkaart gebruiken'
                    : requiredSubscriptions > 1
                        ? 'Strippenkaarten aanschaffen'
                        : 'Strippenkaart aanschaffen');
                if (requiresAdditionalPayment && !newPaymentMethod.endsWith('addSubscription') && !newPaymentMethod.endsWith('addPayment')) {
                    setEnableSubmit(false);
                }
            }
            else if (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('membership')) {
                let subscriptionId = parseInt(newPaymentMethod.substring(11));
                let usable = (_b = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.id === subscriptionId && subscription.subscriptionId);
                if (usable) {
                    setSubmitLabel(newPaymentMethod.endsWith('addPayment')
                        ? 'Abonnement gebruiken en restant afrekenen'
                        : requiresProductPayment(usable)
                            ? 'Abonnement gebruiken en producten afrekenen'
                            : 'Abonnement gebruiken');
                }
                else {
                    let membership = (_c = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _c === void 0 ? void 0 : _c.find((subscription) => subscription.id === subscriptionId);
                    setSubmitLabel((membership === null || membership === void 0 ? void 0 : membership.mandatable) ? 'Volgende stap' : 'Abonnement aanschaffen');
                }
                setSubmitMode("submit");
            }
            else {
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
        }
        else if (hasValidAlternates) {
            if (newPaymentMethod === 'none') {
                setSubmitMode("submit");
                setSubmitLabel('Afspraak bevestigen');
            }
            else if (newPaymentMethod === 'alternate') {
                if (flow) {
                    flow === null || flow === void 0 ? void 0 : flow.setBackCallback(backCallback);
                }
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
            else if (newPaymentMethod === 'external') {
                if (flow) {
                    flow === null || flow === void 0 ? void 0 : flow.setBackCallback(backCallback);
                }
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
            else {
                setSubmitMode("submit");
                setSubmitLabel(hasAppointmentProducts ? 'Afspraak afrekenen' : 'Afrekenen');
            }
        }
        else {
            // there are no subscriptions or memberships
            setSubmitMode("submit");
            let hasSubscription = cart.lines.find((line) => {
                var _a;
                if (line.productType === "subscription" || line.productType === "membership") {
                    let lineProduct = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => {
                        return subscription.product_id == line.productId;
                    });
                    if (lineProduct)
                        return lineProduct.type === "subscription";
                }
                return false;
            });
            let hasMembership = cart.lines.find((line) => {
                var _a;
                if (line.productType === "subscription" || line.productType === "membership") {
                    let lineProduct = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => {
                        return subscription.product_id == line.productId;
                    });
                    if (lineProduct)
                        return lineProduct.type === "membership";
                }
                return false;
            });
            let label = "Afrekenen";
            if (subTotal > 0) {
                if (hasAppointmentProducts) {
                    if (hasSubscription) {
                        label = 'Strippenkaart aanschaffen';
                    }
                    else if (hasMembership) {
                        label = 'Abonnement aanschaffen';
                    }
                    else {
                        label = 'Afspraak afrekenen';
                    }
                }
                else {
                    label = 'Afrekenen';
                }
            }
            else {
                label = 'Afspraak bevestigen';
            }
            setSubmitLabel(label);
            setPaymentMethod(subTotal > 0
                ? newPaymentMethod
                : "none");
        }
    };
    useEffect(() => {
        if (subTotal === 0 && showExternals) {
            setSubmitMode("submit");
            setSubmitLabel(hasAppointmentProducts ? 'Afspraak bevestigen' : 'Bevestigen');
            // todo: figure out how the old widget deals with this
            setPaymentMethod("none");
            setEnableSubmit(true);
        }
    }, [subTotal]);
    const onError = (errors) => {
    };
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        verticalAlign: 'top',
        textAlign: 'right',
        minWidth: '4em'
    }));
    const PaymentOption = ({ subscription, provider, variant }) => {
        if (provider) {
            if (provider.key === 'external') {
                return _jsx(PaymentOptionProviderExternal, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (provider.key === 'alternate') {
                return _jsx(PaymentOptionProviderAlternate, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (provider.key === 'none') {
                return _jsx(PaymentOptionProviderNone, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (showExternals) {
                return _jsx(PaymentOptionProviderRemote, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (showAlternates) {
                return _jsx(PaymentOptionProviderAlternates, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else {
                return _jsx(_Fragment, { children: provider.key });
            }
        }
        if (subscription) {
            if (subscription.subscriptionId) {
                if (showMoreAlternates || showExternals) {
                    return _jsx(_Fragment, {});
                }
                // we own this
                if (subscription.type === 'membership') {
                    return _jsx(PaymentOptionMembershipUse, { subscription: subscription, addPayment: variant === 'addPayment', exceeded: variant === 'addMembership', invoiceTotal: uncoveredTotalOf(subscription), addProductPayment: variant === 'full' && requiresProductPayment(subscription), providerLabel: productPaymentProviderName() });
                }
                else if (subscription.type === 'subscription') {
                    if (subscriptionRequiresAdditionalPayment(subscription)) {
                        let requires = booking.appointments.length * subscription.charge, requiredCredits = requires - subscription.balance, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                        return variant === "addPayment"
                            ? _jsx(PaymentOptionSubscriptionUse, { subscription: subscription, addPayment: true, invoiceTotal: subscriptionUncoveredTotalOf(subscription) })
                            : _jsx(PaymentOptionSubscriptionUse, { subscription: subscription, addSubscription: true, invoiceTotal: subscription.sellPrice * requiredSubscriptions, addSubscriptions: requiredSubscriptions });
                    }
                    else {
                        return _jsx(PaymentOptionSubscriptionUse, { subscription: subscription, addProductPayment: requiresProductPayment(subscription), providerLabel: productPaymentProviderName() });
                    }
                }
            }
            else if (showMoreAlternates) {
                // we don't own this
                let requiredCredits = booking.appointments.length * subscription.charge, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                if (subscription.type === 'subscription' && subscription.buyable) {
                    return _jsx(PaymentOptionSubscriptionBuy, { subscription: subscription, addSubscription: true, invoiceTotal: subscription.sellPrice * requiredSubscriptions, addSubscriptions: requiredSubscriptions });
                }
                else if (subscription.type === 'membership' && subscription.buyable && variant === "full") {
                    return _jsx(PaymentOptionMembershipBuy, { subscription: subscription });
                }
                else if (subscription.type === 'membership' && subscription.mandatable) {
                    return _jsx(PaymentOptionMembershipMandate, { subscription: subscription });
                }
            }
        }
        return (_jsx(_Fragment, {}));
    };
    useEffect(() => {
        var _a, _b, _c;
        if (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription_')) {
            let _subtotal = 0;
            let _rows = [];
            let appointmentProductLines = cart.lines.filter((line) => line.productType === 'appointment');
            if (appointmentProductLines.length === 0) {
                let appointment = undefined;
                if ((booking === null || booking === void 0 ? void 0 : booking.appointments) && (booking === null || booking === void 0 ? void 0 : booking.appointments.length) > 0) {
                    appointment = booking.appointments[0];
                    _rows.push(createRow([''], appointment.price, booking.appointments.length, 0, //   21,
                    "appointment", "appointment"));
                }
            }
            let firstAppointment = true;
            appointmentProductLines.map((line) => {
                var _a;
                // should always be 1 , for now 
                // nope, we have multiple now
                if (line.productType === 'appointment') {
                    let appointment = booking.appointments.find((appointment) => appointment.id === line.appointmentId);
                    if (!appointment) {
                        appointment = booking.appointments[0];
                    }
                    if (appointment) {
                        if (firstAppointment) {
                            _rows.push(createRow([''], (_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price, booking.appointments.length, 0, //   21,
                            "appointment", "appointment"));
                            //}
                            firstAppointment = false;
                        }
                    }
                }
            });
            let otherProductLines = cart.lines.filter((line) => line.productType !== 'appointment');
            otherProductLines.map((line) => {
                var _a, _b, _c;
                _rows.push(createRow([
                    line.name
                ], (_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price, (_b = line.quantity) !== null && _b !== void 0 ? _b : 1, 0, //   21,
                line.productType, (_c = line.origin) !== null && _c !== void 0 ? _c : "optionalPurchase"));
            });
            let subscription = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === parseInt(displayPaymentMethod.substring(13)));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                if (subscription.charge) {
                    let appointmentsCount = (_b = booking === null || booking === void 0 ? void 0 : booking.appointments.length) !== null && _b !== void 0 ? _b : 0, requires = subscription.charge * appointmentsCount;
                    if (requires > subscription.balance) {
                        // todo: is a balance of -1 a thing?
                        setRequiresAdditionalPayment(true);
                        // An appointment is never split between subscription credit and cash -
                        // it is paid in full one way or the other. Only whole appointments the
                        // current balance can fully cover are "free"; the rest are paid in full,
                        // in cash. This feeds the addPayment radio's own "restant" text - the
                        // addSubscription one computes its own total below and ignores this.
                        let coveredByBalance = Math.min(appointmentsCount, Math.floor(subscription.balance / subscription.charge)), uncoveredCount = appointmentsCount - coveredByBalance, appointmentRow = _rows.find((row) => row.type === "appointment");
                        _subtotal = uncoveredCount * ((_c = appointmentRow === null || appointmentRow === void 0 ? void 0 : appointmentRow.unit) !== null && _c !== void 0 ? _c : 0);
                        setInvoiceTotal(_subtotal);
                    }
                }
            }
        }
        else {
            setRequiresAdditionalPayment(false);
        }
    }, [displayPaymentMethod, cart.lines]);
    // 'Niet online afrekenen' belongs on this first step only when a subscription/strippenkaart or
    // not paying online at all is the entire choice. With any other method in the list it is one of
    // the options behind "toon overige", where the provider loop renders it.
    const onlyNoneAndAlternate = paymentMethods.length === 2
        && paymentMethods.includes('none')
        && paymentMethods.includes('alternate');
    // The membership that is over its limits, for the cart to show which appointments it does not pay for
    const limitedMembership = (_b = consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.type === 'membership' && subscription.subscriptionId && limitStateOf(subscription) !== 'full');
    const membershipCartCoverage = limitedMembership
        ? { state: limitStateOf(limitedMembership), covered: limitedMembership.coverage }
        : undefined;
    return (_jsx(BoxContainer, { sx: {
            paddingTop: 2
        }, children: _jsxs(Box, { sx: {}, children: [paymentError && (_jsx(ErrorAlert, { messages: paymentError, setter: setPaymentError, scrollIntoView: props.scrollErrorsIntoView })), _jsx(ErrorAlert, { messages: errorValidate, setter: setErrorValidate, scrollIntoView: props.scrollErrorsIntoView }), _jsx(SuccessAlert, { messages: successValidate, setter: setSuccessValidate }), !loader && !isPaying && (_jsx(CartComponent, { canDeposit: canDeposit && showExternals, onEdit: editAppointments, useDeposit: useDeposit, setter: setUseDeposit, fees: cartFees, deposit: depositPrice, locked: false, paymentMethod: displayPaymentMethod, membershipCoverage: membershipCartCoverage, canRedeem: hasCheckout && showExternals && !cart.lines.find((line) => ["subscription", "membership"].includes(line.productType)), setSubTotal: setSubTotal, setErrorValidate: setErrorValidate, setSuccessValidate: setSuccessValidate, errorValidate: errorValidate, successValidate: successValidate })), paymentMethods && paymentMethods.length === 0 && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('Ophalen van betaalmethodes...') }), _jsx(LinearProgress, {})] })), cart.order_id && isPaying && displayPaymentMethod !== "none" && subTotal > 0 && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(PollingPaymentStatus, { method: (_c = pollingMethod !== null && pollingMethod !== void 0 ? pollingMethod : displayPaymentMethod) !== null && _c !== void 0 ? _c : 'ideal', paymentId: cart.order_id, transaction: {
                                redirectUrl: redirectUri
                            } }), _jsx(Button, { type: "button", fullWidth: true, sx: {
                                my: 4
                            }, variant: "contained", onClick: backCallback, children: t("Betaling afbreken", "payment") })] })), _jsx(FormContainer, { formContext: formContext, onSuccess: onSubmit, onError: onError, children: _jsxs(FormErrorProvider, { onError: (error) => {
                            if (error.type === 'validate') {
                                console.log(error);
                            }
                            return t(error.type, 'error');
                        }, children: [error && (_jsx(ErrorAlert, { messages: error, setter: setError, scrollIntoView: props.scrollErrorsIntoView })), paymentMethods && paymentMethods.length > 0 && !isPaying && (_jsxs(_Fragment, { children: [loader && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('Betaling wordt gestart...') }), _jsx(LinearProgress, {})] })), !loader && !updateCartQuery.isFetching && (_jsx(_Fragment, { children: cart.order_id && (_jsxs(_Fragment, { children: [requiresAdditionalPayment && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addPayment')) && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addSubscription')) && (_jsx(Alert, { sx: { my: 2, mx: 1 }, severity: "warning", children: _jsx(Typography, { children: ts('Let op: je gekozen strippenkaart heeft niet voldoende strippen om de %amount% %amount=1?afspraak:afspraken% te betalen', { amount: booking.appointments.length }) }) })), (!showExternals || (showExternals && subTotal > 0)) && (_jsx(Typography, { variant: "h6", sx: { marginLeft: 1, marginTop: 0, fontSize: '1rem' }, children: t('Hoe wil je betalen?') })), _jsx(RadioGroup, { name: "paymentMethod", onChange: onChange, children: _jsx(TableContainer, { component: Paper, children: _jsx(Table, { sx: { width: '100%' }, size: "small", "aria-label": "a dense table", children: _jsxs(TableBody, { children: [consumer.subscriptions && !cart.lines.find((line) => ['subscription', 'membership'].includes(line.productType)) && (_jsx(_Fragment, { children: consumer.subscriptions.map((subscription) => {
                                                                            if (showExternals) {
                                                                                // only show external payment providers
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (subscription.type === 'subscription' && subscription.charge > subscription.balance && subscription.balance !== -1) {
                                                                                // not enough credits, probably already filtered on server
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (showAlternates && !subscription.subscriptionId) {
                                                                                // don't show buy a subscription
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (showMoreAlternates && subscription.subscriptionId) {
                                                                                // don't show use a subscription
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (subscription.type === 'membership' && subscription.subscriptionId && limitStateOf(subscription) !== 'full') {
                                                                                // Over a limit for some of the appointments. Using the membership for all of them
                                                                                // is not possible, and is shown as an option that cannot be chosen (addMembership).
                                                                                // When the app type has an online payment, the membership can pay for what is
                                                                                // within its limits and the rest is paid online (addPayment), the way a
                                                                                // subscription that runs out offers it.
                                                                                let limitState = limitStateOf(subscription);
                                                                                return (_jsxs(Fragment, { children: [_jsx(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { disabled: true, value: `${subscription.type}_${subscription.id}_addMembership`, 
                                                                                                    // selected for as long as nothing else is: the order lines show it
                                                                                                    control: _jsx(Radio, { checked: !displayPaymentMethod || displayPaymentMethod === 'alternate' }), label: _jsx(PaymentOption, { subscription: subscription, variant: "addMembership" }) }) }) }, `${subscription.type}_${subscription.id}_addMembership`), limitState === 'addPayment' && (_jsx(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}_addPayment`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "addPayment" }) }) }) }, `${subscription.type}_${subscription.id}_addPayment`))] }, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (subscription.type === "subscription" && subscriptionRequiresAdditionalPayment(subscription)) {
                                                                                return (_jsxs(Fragment, { children: [subscription.sellPrice > 0 && (_jsxs(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}_addSubscription`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "addSubscription" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}_addSubscription`)), _jsxs(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}_addPayment`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "addPayment" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}_addPayment`)] }, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            return (_jsx(Fragment, { children: _jsxs(TableRow, { sx: {
                                                                                        backgroundColor: theme.palette.background.default,
                                                                                        '&:last-child td, &:last-child th': { border: 0 }
                                                                                    }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "full" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}`) }, `${subscription.type}_${subscription.id}`));
                                                                        }) })), hasValidMoreAlternates && !showMoreAlternates && !showExternals && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'alternate', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'alternate',
                                                                                        icon: '',
                                                                                        label: 'Toon overige abonnementen en strippenkaarten'
                                                                                    } }) }) }) }, 'alternate')), onlyNoneAndAlternate && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'none', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'none',
                                                                                        icon: '',
                                                                                        label: 'Niet online afrekenen'
                                                                                    } }) }) }) }, 'none')), hasValidExternals && !showExternals && (!showMoreAlternates || (showMoreAlternates && !hasValidUsables)) && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'external', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'external',
                                                                                        icon: '',
                                                                                        label: 'Losse afspraak maken'
                                                                                    } }) }) }) }, 'external')), hasValidExternals && showExternals && (subTotal > 0 || displayPaymentMethod === "none") && (_jsx(_Fragment, { children: payment.providers.map((provider) => {
                                                                            if (provider.key.startsWith('subscription_') || provider.key.startsWith('membership_')) {
                                                                                return (_jsx(Fragment, {}, provider.key));
                                                                            }
                                                                            if (subTotal === 0 && paymentMethod !== "none") {
                                                                                // nothing left to pay
                                                                                return (_jsx(Fragment, {}, provider.key));
                                                                            }
                                                                            // "<provider>_productsOnly": pays the required product(s) with this
                                                                            // provider while the appointment is skipped/never payable online (see
                                                                            // CheckoutController::_getCheckout()). Not one of `payment.providers`'
                                                                            // own entries - it rides along on the real provider's icon/label, one
                                                                            // possible extra row per provider, distinct from that provider's own
                                                                            // "pay everything" row below.
                                                                            const productsOnlyKey = `${provider.key}_productsOnly`;
                                                                            const showProductsOnly = provider.key !== 'directdebit'
                                                                                && paymentMethods.includes(productsOnlyKey);
                                                                            const showFull = paymentMethods.includes(provider.key) && provider.key !== 'directdebit';
                                                                            return (_jsxs(Fragment, { children: [showProductsOnly && (_jsxs(TableRow, { sx: {
                                                                                            backgroundColor: theme.palette.background.default,
                                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                                        }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: productsOnlyKey, control: _jsx(Radio, {}), label: _jsx(PaymentOptionProviderProductsOnly, { provider: provider, useDeposit: useDeposit, fees: fees }) }) }), _jsx(StyledTableCell, { children: provider.icon && (_jsx("img", { src: provider.icon, style: {
                                                                                                        height: '3em',
                                                                                                        aspectRatio: '4 / 3',
                                                                                                        boxSizing: 'border-box'
                                                                                                    } })) })] }, productsOnlyKey)), showFull && (_jsxs(TableRow, { sx: {
                                                                                            backgroundColor: theme.palette.background.default,
                                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                                        }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: provider.key === 'none' ? 1 : 2, children: _jsx(FormControlLabel, { value: provider.key, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: provider }) }) }), provider.key !== 'none' && (_jsx(StyledTableCell, { children: provider.icon && (_jsx("img", { src: provider.icon, style: {
                                                                                                        height: '3em',
                                                                                                        aspectRatio: '4 / 3',
                                                                                                        boxSizing: 'border-box'
                                                                                                    } })) }))] }, provider.key))] }, provider.key));
                                                                        }) }))] }) }) }) })] })) }))] }))] }) }), updateCartQuery.isFetching && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { marginTop: 4, marginBottom: 2, textAlign: 'center' }, children: t('De betaalmethode wordt bijgewerkt...') }), _jsx(LinearProgress, {})] })), !loader && !isPaying && (_jsxs("form", { action: submitUrl.href, target: '_blank', method: "POST", onSubmit: prepareSubmit, children: [_jsx("input", { type: "hidden", id: "payload", name: "payload" }), _jsx("input", { type: "hidden", id: "csrfToken", name: "token" }), _jsx("input", { type: "hidden", id: "secureToken", name: "secureToken" }), _jsx(Box, { sx: {}, children: _jsx(Button, { type: "submit", fullWidth: true, sx: {
                                    my: 4
                                }, variant: "contained", disabled: !enableSubmit || updateCartQuery.isFetching || abortQuery.isFetching, children: t(submitLabel, "payment") }) })] }))] }) }));
}
